/**
 * Read the live design tokens off the DOM so the mood field is painted in the
 * product's own colours rather than a second, drifting palette.
 *
 * Deliberately three-free: this module is imported eagerly by the canvas
 * component, while three itself is behind a dynamic import. Nothing here may
 * grow a three dependency.
 *
 * Colours come back as plain [r, g, b] triples in 0..1 sRGB — the same space
 * CSS blends in — so the shader output composites over the card exactly as a
 * CSS gradient would. (This is also why the shaders never touch THREE.Color:
 * its colour-management round-trip would quietly shift every token.)
 */

const FALLBACK = {
  mood: ["#465878", "#6f7c95", "#a39b92", "#c0a277", "#d4a45f"],
  accent: "#3e7f6c",
  surface: "#fffdfa",
};

/** Parse `#abc`, `#aabbcc`, `rgb(…)` or `rgba(…)` into [r, g, b] in 0..1. */
export function parseColor(input, fallback = "#000000") {
  const value = String(input ?? "").trim();
  const parsed = parseRaw(value);
  return parsed || parseRaw(fallback) || [0, 0, 0];
}

function parseRaw(value) {
  if (!value) return null;

  if (value[0] === "#") {
    const hex = value.slice(1);
    if (hex.length === 3 || hex.length === 4) {
      const n = [0, 1, 2].map((i) => parseInt(hex[i] + hex[i], 16));
      return n.some(Number.isNaN) ? null : n.map((c) => c / 255);
    }
    if (hex.length === 6 || hex.length === 8) {
      const n = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
      return n.some(Number.isNaN) ? null : n.map((c) => c / 255);
    }
    return null;
  }

  const fn = value.match(/^rgba?\(([^)]+)\)$/i);
  if (fn) {
    const parts = fn[1].split(/[\s,/]+/).filter(Boolean).slice(0, 3);
    if (parts.length < 3) return null;
    const n = parts.map((p) =>
      p.endsWith("%") ? (parseFloat(p) / 100) * 255 : parseFloat(p)
    );
    return n.some(Number.isNaN) ? null : n.map((c) => Math.min(1, Math.max(0, c / 255)));
  }

  return null;
}

/** Rough perceptual luminance — only used to decide "is this a dark theme". */
function luminance([r, g, b]) {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Mix two [r,g,b] triples. */
export function mixColor(a, b, t) {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/**
 * Snapshot the tokens that matter to the field, resolved against `el` so any
 * scoped theme override (`:root[data-theme]`, a preview wrapper) is honoured.
 */
export function readPalette(el) {
  const styles = el && typeof getComputedStyle === "function" ? getComputedStyle(el) : null;
  const token = (name, fallback) => {
    const raw = styles ? styles.getPropertyValue(name) : "";
    return parseColor(raw && raw.trim() ? raw : fallback, fallback);
  };

  const surface = token("--surface", FALLBACK.surface);
  const mood = FALLBACK.mood.map((fallback, i) => token(`--mood-${i + 1}`, fallback));

  return {
    mood,
    accent: token("--accent", FALLBACK.accent),
    surface,
    dark: luminance(surface) < 0.4,
  };
}
